// Order resources (sales, purchases, quotations, return orders, recurring sales):
// legacy list columns + the full OrderEditor (features/orders) as custom form.
// Row actions (email, download, void, payments, deliveries, duplicate,
// quotation → sale) are layered on top by 20-order-actions.ts.
//
// Gap IDs: S1 (finalize), S2 (sale types), S3 (editor), S4/S13 (lists, bulk
// delete), S7 (return orders), S11 (recurring repeat values).

import { t } from '../../i18n';
import { buildOrderPayload, ORDER_KINDS, type OrderResource, stateFromRecord } from '../../features/orders/orderModel';
import { OrderEditor } from '../../features/orders/OrderEditor';
import { moneyValue, orderDetailSections, relationName, yesNoValue } from '../configHelpers';
import type { CrudColumn, CrudRecord, CrudResourceConfig } from '../types';

type Override = (base: CrudResourceConfig | undefined) => CrudResourceConfig;

const userName = (row: CrudRecord) => relationName(row.user);
const party = (key: 'customer' | 'supplier') => (row: CrudRecord) => {
  const value = row[key] as { name?: string; company?: string } | undefined;
  if (!value) return '-';
  return value.company && value.name && value.company !== value.name
    ? `${value.name} (${value.company})`
    : String(value.name ?? value.company ?? '-');
};

const col = {
  date: { key: 'date', label: 'Date', sortable: true } as CrudColumn,
  reference: { key: 'reference', label: 'Reference', sortable: true } as CrudColumn,
  orderNumber: { key: 'orderNumber', label: 'Order Number', sortable: true } as CrudColumn,
  ticketNumber: { key: 'ticketNumber', label: 'Ticket Number', sortable: true } as CrudColumn,
  customer: { key: 'customer', label: 'Customer', render: party('customer') } as CrudColumn,
  supplier: { key: 'supplier', label: 'Supplier', render: party('supplier') } as CrudColumn,
  draft: {
    key: 'draft',
    label: 'Draft',
    sortable: true,
    align: 'center',
    render: (row) => yesNoValue(Number(row.draft)),
    tone: (row) => (Number(row.draft) ? 'warning' : 'default')
  } as CrudColumn,
  paid: {
    key: 'paid',
    label: 'Paid',
    sortable: true,
    align: 'center',
    render: (row) => yesNoValue(Number(row.paid)),
    tone: (row) => (Number(row.paid) ? 'success' : 'default')
  } as CrudColumn,
  void: {
    key: 'void',
    label: 'Void',
    sortable: true,
    align: 'center',
    render: (row) => yesNoValue(Number(row.void)),
    tone: (row) => (Number(row.void) ? 'error' : 'default')
  } as CrudColumn,
  grandTotal: {
    key: 'grand_total',
    label: 'Grand Total',
    sortable: true,
    align: 'right',
    render: (row) => moneyValue(row.grand_total)
  } as CrudColumn,
  attachments: {
    key: 'attachments',
    label: 'Attachments',
    align: 'center',
    render: (row) => (Array.isArray(row.attachments) && row.attachments.length ? String(row.attachments.length) : '-')
  } as CrudColumn,
  createdAt: { key: 'created_at', label: 'Created At', sortable: true } as CrudColumn,
  createdBy: { key: 'user', label: 'Created By', render: userName } as CrudColumn,
  total: { key: 'total', label: 'Total', align: 'right', render: (row) => moneyValue(row.total) } as CrudColumn,
  itemTax: { key: 'item_tax_amount', label: 'Item Tax', align: 'right', render: (row) => moneyValue(row.item_tax_amount) } as CrudColumn,
  orderTax: {
    key: 'order_tax_amount',
    label: 'Order Tax',
    align: 'right',
    render: (row) => moneyValue(row.order_tax_amount)
  } as CrudColumn,
  discount: { key: 'discount_amount', label: 'Discount', align: 'right', render: (row) => moneyValue(row.discount_amount) } as CrudColumn,
  shipping: { key: 'shipping', label: 'Shipping', align: 'right', render: (row) => moneyValue(row.shipping) } as CrudColumn,
  details: { key: 'details', label: 'Details', render: (row) => (row.details ? String(row.details).slice(0, 80) : '-') } as CrudColumn
};

// Finalize (S1): the engine PUTs normalizeBeforeSave({ ...GET record, draft: 0 }).
// Rebuild the full legacy payload from the stored record so the items, taxes and
// discounts survive, and honour the draft value the engine passes.
function finalizePayload(resource: OrderResource) {
  return (payload: Record<string, unknown>) => {
    const kind = ORDER_KINDS[resource];
    const state = stateFromRecord(payload, kind, false);
    const extras =
      payload.extra_attributes && typeof payload.extra_attributes === 'object' && !Array.isArray(payload.extra_attributes)
        ? Object.keys(payload.extra_attributes as object)
        : [];
    return buildOrderPayload(state, kind, {
      taxes: [],
      ctx: {},
      useStoredTaxes: true,
      customFieldSlugs: extras,
      draftOverride: payload.draft === 0 || payload.draft === '0' || payload.draft === false ? 0 : 1
    });
  };
}

function orderConfig(
  base: CrudResourceConfig | undefined,
  resource: OrderResource,
  patch: Partial<CrudResourceConfig>
): CrudResourceConfig {
  return {
    ...(base ?? { resource, title: resource, singular: resource, orderBy: 'date desc', columns: [], fields: [] }),
    resource,
    orderBy: 'date desc',
    // Server-side search over the model $searchable columns (reference, party, user…).
    searchMode: 'server',
    searchField: undefined,
    localSearchLimit: undefined,
    requiresLocation: true,
    customFields: true,
    showAttachments: true,
    customForm: OrderEditor,
    pageSizes: [10, 25, 50, 100],
    rowTone: (row) => (Number(row.void) ? 'error' : undefined),
    fields: [],
    normalizeBeforeSave: finalizePayload(resource),
    ...patch
  };
}

export const overrides: Record<string, Override> = {
  sales: (base) =>
    orderConfig(base, 'sales', {
      canFinalize: true,
      bulkDelete: true,
      columns: [
        col.date,
        col.reference,
        col.orderNumber,
        col.ticketNumber,
        col.customer,
        col.draft,
        col.paid,
        col.void,
        col.grandTotal,
        col.attachments,
        col.createdAt,
        col.createdBy,
        col.total,
        col.itemTax,
        col.orderTax,
        col.discount,
        col.shipping,
        col.details
      ],
      detailSections: orderDetailSections('customer')
    }),
  purchases: (base) =>
    orderConfig(base, 'purchases', {
      canFinalize: true,
      bulkDelete: true,
      columns: [
        col.date,
        col.reference,
        col.supplier,
        col.draft,
        col.paid,
        col.void,
        col.grandTotal,
        col.attachments,
        col.createdAt,
        col.createdBy,
        col.total,
        col.itemTax,
        col.orderTax,
        col.discount,
        col.shipping,
        col.details
      ],
      detailSections: orderDetailSections('supplier')
    }),
  quotations: (base) =>
    orderConfig(base, 'quotations', {
      bulkDelete: true,
      // Quotations have no void flag.
      rowTone: undefined,
      columns: [
        col.date,
        col.reference,
        col.customer,
        col.grandTotal,
        col.attachments,
        col.createdAt,
        col.createdBy,
        col.total,
        col.itemTax,
        col.orderTax,
        col.discount,
        col.shipping,
        col.details
      ],
      detailSections: orderDetailSections('customer')
    }),
  return_orders: (base) =>
    orderConfig(base, 'return_orders', {
      permissionResource: base?.permissionResource ?? 'return-orders',
      canFinalize: true,
      bulkDelete: true,
      rowTone: undefined,
      columns: [
        col.date,
        col.reference,
        { key: 'type', label: 'Type', sortable: true, render: (row) => (row.type ? t(`orders.type.${String(row.type)}`) : '-') },
        { key: 'party', label: 'From / To', render: (row) => (row.type === 'purchase' ? party('supplier')(row) : party('customer')(row)) },
        {
          key: 'deduct_from_register',
          label: 'Register',
          sortable: true,
          align: 'center',
          render: (row) => yesNoValue(Number(row.deduct_from_register))
        },
        col.draft,
        col.grandTotal,
        col.attachments,
        col.createdAt,
        col.createdBy,
        col.total,
        col.itemTax,
        col.orderTax,
        col.discount,
        col.details
      ],
      detailSections: orderDetailSections('customer')
    }),
  recurring_sales: (base) =>
    orderConfig(base, 'recurring_sales', {
      permissionResource: base?.permissionResource ?? 'recurring-sales',
      orderBy: 'start_date desc',
      canFinalize: true,
      rowTone: undefined,
      // ?in_days=N (dashboard "due soon" links) → RecurringSale::dueDays().
      passthroughQueryParams: ['in_days'],
      columns: [
        { key: 'start_date', label: 'Start Date', sortable: true },
        { key: 'repeat', label: 'Repeat', sortable: true, render: (row) => (row.repeat ? t(`orders.repeat.${String(row.repeat)}`) : '-') },
        col.reference,
        { key: 'create_before', label: 'Create Before', sortable: true, align: 'right' },
        col.customer,
        col.draft,
        col.grandTotal,
        col.attachments,
        col.createdAt,
        { key: 'last_created_at', label: 'Last Created At', sortable: true },
        col.createdBy,
        col.total,
        col.itemTax,
        col.orderTax,
        col.discount,
        col.shipping,
        col.details
      ],
      detailSections: orderDetailSections('customer')
    })
};
